import { useEffect, useRef } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Separator } from "@/components/ui/separator";

const Personvern = () => {
  const cookieDeclarationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cookieDeclarationRef.current) {
      const script = document.createElement("script");
      script.id = "CookieDeclaration";
      script.src = "https://consent.cookiebot.com/51ed687d-94db-4030-b266-809c203f1d54/cd.js";
      script.type = "text/javascript";
      script.async = true;
      cookieDeclarationRef.current.appendChild(script);
    }
  }, []);

  return (
    <main className="overflow-hidden">
      <Header />
      <div className="pt-32 pb-20 bg-background">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <h1 className="font-serif text-3xl md:text-4xl text-foreground mb-2">Personvern & Cookies</h1>
          <p className="text-muted-foreground font-sans text-sm mb-8">Sist oppdatert: {new Date().toLocaleDateString('nb-NO', { day: 'numeric', month: 'long', year: 'numeric' })}</p>

          <div className="prose prose-neutral max-w-none font-sans text-foreground/80 space-y-10">
            {/* 1. Hvem vi er */}
            <section>
              <h2 className="font-serif text-xl text-foreground">1. Hvem vi er</h2>
              <p>
                Bunadstuggu er behandlingsansvarlig for personopplysninger som samles inn via denne nettsiden.
              </p>
              <ul className="list-none space-y-1 pl-0">
                <li><strong>Bedrift:</strong> Bunadstuggu</li>
                <li><strong>Adresse:</strong> Kirkegata 38, 7600 Levanger</li>
                <li><strong>Telefon:</strong> 480 09 271</li>
              </ul>
              <p>
                Har du spørsmål om personvern eller ønsker å utøve dine rettigheter, kan du kontakte oss
                på telefon eller ved å møte opp i butikken.
              </p>
            </section>

            <Separator />

            {/* 2. Hvilke opplysninger vi samler inn */}
            <section>
              <h2 className="font-serif text-xl text-foreground">2. Hvilke opplysninger vi samler inn</h2>
              <p>Vi samler inn følgende personopplysninger gjennom nettsiden:</p>

              <h3 className="font-serif text-lg text-foreground mt-4">Kontaktskjema</h3>
              <p>
                Når du sender oss en melding via kontaktskjemaet, samler vi inn e-postadressen din og
                innholdet i meldingen. Denne informasjonen brukes utelukkende for å besvare din henvendelse.
              </p>

              <h3 className="font-serif text-lg text-foreground mt-4">Teknisk informasjon</h3>
              <p>
                Når du besøker nettsiden, kan webserveren automatisk logge teknisk informasjon som
                IP-adresse, nettlesertype, operativsystem, tidspunkt for besøket og hvilke sider som ble besøkt.
                Denne informasjonen brukes for å sikre stabil og sikker drift av nettsiden.
              </p>

              <h3 className="font-serif text-lg text-foreground mt-4">Ansattinnlogging</h3>
              <p>
                Nettsiden har en innloggingsfunksjon for ansatte som publiserer blogginnhold. Ved innlogging
                lagres e-postadresse og autentiseringsinformasjon for å gi tilgang til administrasjonsfunksjoner.
              </p>
            </section>

            <Separator />

            {/* 3. Hvordan opplysningene brukes */}
            <section>
              <h2 className="font-serif text-xl text-foreground">3. Formål med datainnsamlingen</h2>
              <p>Vi behandler personopplysninger for følgende formål:</p>
              <ul className="space-y-2">
                <li><strong>Besvare henvendelser</strong> – Vi bruker e-postadresse og meldingsinnhold fra kontaktskjemaet for å svare på dine spørsmål.</li>
                <li><strong>Administrasjon av nettside</strong> – Ansattes innloggingsdata brukes for å gi tilgang til publisering av blogginnhold.</li>
                <li><strong>Sikkerhet og drift</strong> – Teknisk informasjon brukes for å sikre at nettsiden fungerer stabilt og trygt.</li>
                <li><strong>Analyse av nettstedstrafikk</strong> – Vi bruker Google Search Console for å forstå hvordan nettsiden presterer i søkeresultater (se punkt 5).</li>
              </ul>
              <p>
                Behandlingsgrunnlaget er GDPR artikkel 6(1)(f) berettiget interesse for drift og sikkerhet,
                og artikkel 6(1)(b) oppfyllelse av avtale for besvarelse av henvendelser.
              </p>
            </section>

            <Separator />

            {/* 4. Bruk av cookies */}
            <section>
              <h2 className="font-serif text-xl text-foreground">4. Bruk av informasjonskapsler (cookies)</h2>

              <h3 className="font-serif text-lg text-foreground mt-4">Hva er cookies?</h3>
              <p>
                Informasjonskapsler (cookies) er små tekstfiler som lagres på din enhet når du besøker en nettside.
                De brukes for å huske innstillinger, forbedre brukeropplevelsen og sikre at nettsiden fungerer korrekt.
              </p>

              <h3 className="font-serif text-lg text-foreground mt-4">Hvilke cookies bruker vi?</h3>
              <p>Vi bruker følgende typer informasjonskapsler:</p>
              <ul className="space-y-2">
                <li><strong>Nødvendige cookies</strong> – Autentisering, sesjonshåndtering og cookiesamtykke. Disse er påkrevd for at nettsiden skal fungere.</li>
                <li><strong>Analyse og statistikk</strong> – Vi bruker Google Tag Manager og Google Analytics for å forstå hvordan besøkende bruker nettsiden. Disse aktiveres kun etter ditt samtykke.</li>
              </ul>

              <h3 className="font-serif text-lg text-foreground mt-4">Cookie-erklæring</h3>
              <p>Nedenfor finner du en fullstendig oversikt over alle cookies som brukes på denne nettsiden:</p>
              <div ref={cookieDeclarationRef} className="mt-4" />
            </section>

            <Separator />

            {/* 5. Tredjepartstjenester */}
            <section>
              <h2 className="font-serif text-xl text-foreground">5. Tredjepartstjenester</h2>

              <h3 className="font-serif text-lg text-foreground mt-4">Google Search Console</h3>
              <p>
                Vi bruker Google Search Console for å analysere hvordan nettsiden vår vises i Googles søkeresultater.
                Google Search Console gir oss informasjon om søkeord som fører til nettsiden, klikkfrekvens og
                tekniske problemer. Denne tjenesten samler ikke inn personopplysninger direkte fra besøkende,
                men bruker aggregert data fra Googles søkeindeks.
              </p>
              <p>
                For mer informasjon om Googles personvernpraksis, se{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                  Googles personvernerklæring
                </a>.
              </p>

              <h3 className="font-serif text-lg text-foreground mt-4">Hosting og databehandling</h3>
              <p>
                Nettsiden er hostet på sikre servere. Data som samles inn gjennom kontaktskjemaet
                og ansattinnlogging lagres i en sikker database med tilgangskontroll og kryptering.
              </p>
            </section>

            <Separator />

            {/* 6. Lagring av data */}
            <section>
              <h2 className="font-serif text-xl text-foreground">6. Lagring og beskyttelse av data</h2>

              <h3 className="font-serif text-lg text-foreground mt-4">Hvordan vi beskytter dine data</h3>
              <p>Vi tar datasikkerhet på alvor og har iverksatt følgende tiltak:</p>
              <ul className="space-y-2">
                <li>All kommunikasjon med nettsiden skjer over kryptert tilkobling (HTTPS/SSL).</li>
                <li>Tilgang til personopplysninger er begrenset til autorisert personell.</li>
                <li>Databasen er beskyttet med tilgangskontroll og sikkerhetspolicyer (Row-Level Security).</li>
                <li>Passord lagres aldri i klartekst, men hashes med sikre algoritmer.</li>
              </ul>

              <h3 className="font-serif text-lg text-foreground mt-4">Lagringstid</h3>
              <ul className="space-y-2">
                <li><strong>Kontaktskjema:</strong> Henvendelser lagres så lenge det er nødvendig for å besvare og følge opp din forespørsel, og slettes deretter.</li>
                <li><strong>Ansattkontoer:</strong> Innloggingsdata lagres så lenge ansettelsesforholdet varer, og slettes når kontoen ikke lenger er i bruk.</li>
                <li><strong>Tekniske logger:</strong> Serverlogger slettes automatisk etter en begrenset periode (normalt 30–90 dager).</li>
                <li><strong>Cookies:</strong> Session-cookies slettes automatisk når nettleserøkten avsluttes eller ved utlogging.</li>
              </ul>
            </section>

            <Separator />

            {/* 7. Dine rettigheter */}
            <section>
              <h2 className="font-serif text-xl text-foreground">7. Dine rettigheter</h2>
              <p>
                I henhold til GDPR og norsk personvernlovgivning har du følgende rettigheter:
              </p>
              <ul className="space-y-2">
                <li><strong>Rett til innsyn</strong> – Du kan be om å få vite hvilke personopplysninger vi har lagret om deg.</li>
                <li><strong>Rett til retting</strong> – Du kan be om at uriktige opplysninger blir rettet.</li>
                <li><strong>Rett til sletting</strong> – Du kan be om at dine personopplysninger slettes, med mindre vi har en lovmessig plikt til å beholde dem.</li>
                <li><strong>Rett til begrensning</strong> – Du kan be om at behandlingen av dine opplysninger begrenses.</li>
                <li><strong>Rett til dataportabilitet</strong> – Du kan be om å få dine data utlevert i et maskinlesbart format.</li>
                <li><strong>Rett til å protestere</strong> – Du kan protestere mot behandling av dine personopplysninger.</li>
              </ul>
              <p>
                For å utøve dine rettigheter, kontakt oss via kontaktinformasjonen nedenfor.
                Vi vil besvare henvendelsen din innen 30 dager.
              </p>
            </section>

            <Separator />

            {/* 8. Kontaktinformasjon */}
            <section>
              <h2 className="font-serif text-xl text-foreground">8. Kontaktinformasjon</h2>
              <p>
                Dersom du har spørsmål om denne personvernerklæringen, ønsker innsyn i dine data,
                eller vil be om sletting, kan du kontakte oss:
              </p>
              <ul className="list-none space-y-1 pl-0">
                <li><strong>Bedrift:</strong> Bunadstuggu</li>
                <li><strong>Adresse:</strong> Kirkegata 38, 7600 Levanger</li>
                <li><strong>Telefon:</strong> 480 09 271</li>
              </ul>
              <p>
                Du har også rett til å klage til Datatilsynet dersom du mener at vi ikke behandler
                dine personopplysninger i samsvar med regelverket. Datatilsynet kan kontaktes via{" "}
                <a href="https://www.datatilsynet.no" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                  www.datatilsynet.no
                </a>.
              </p>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default Personvern;
